import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { FinancialText } from '#components/FinancialText';
import { Page } from '#components/Page';

import { JevCategoryPicker } from './JevCategoryPicker';
import { JevSuggestionCard } from './JevSuggestionCard';

type ReviewState = Awaited<ReturnType<typeof loadReviewState>>;
type MatchesResult = Awaited<ReturnType<typeof loadMatches>>;
type MatchSuggestion = Extract<
  MatchesResult,
  { suggestions: unknown }
>['suggestions'][number];
type MatchTransaction = MatchSuggestion['transactions'][number];

function loadReviewState() {
  return send('jev-review-state');
}

function classifyUncategorized() {
  return send('jev-classify-uncategorized');
}

function loadMatches() {
  return send('jev-match-suggestions');
}

function Transaction({ trans }: { trans: MatchTransaction }) {
  return (
    <View style={{ flex: 1, gap: 2, flexShrink: 0 }}>
      <Text style={{ fontWeight: 600 }}>{trans.description}</Text>
      <Text style={{ color: theme.pageTextSubdued }}>
        {trans.date} · {trans.accountName}
      </Text>
      <FinancialText>{integerToCurrency(trans.amount)}</FinancialText>
    </View>
  );
}

function Card({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        backgroundColor: theme.tableBackground,
        border: '1px solid ' + theme.tableBorder,
        borderRadius: 4,
        padding: 12,
        gap: 10,
        flexShrink: 0,
      }}
    >
      {children}
    </View>
  );
}

export function JevReview() {
  const { t } = useTranslation();
  const [state, setState] = useState<ReviewState | null>(null);
  const [matches, setMatches] = useState<MatchesResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [isClassifying, setIsClassifying] = useState(false);
  const [classifyResult, setClassifyResult] = useState<Awaited<
    ReturnType<typeof classifyUncategorized>
  > | null>(null);
  // The JEV-categorized transaction whose category is being changed
  const [changingId, setChangingId] = useState<string | null>(null);

  useEffect(() => {
    void loadReviewState().then(setState);
  }, []);

  async function refresh() {
    setState(await loadReviewState());
  }

  async function onAcceptCategory(transactionId: string, categoryId: string) {
    setChangingId(null);
    await send('jev-category-accept', { transactionId, categoryId });
    await refresh();
  }

  async function onClassifyAll() {
    setIsClassifying(true);
    setClassifyResult(null);
    const result = await classifyUncategorized();
    setClassifyResult(result);
    setIsClassifying(false);
    await refresh();
  }

  async function onFindMatches() {
    setIsSearching(true);
    setMatches(await loadMatches());
    setIsSearching(false);
  }

  function dropMatch(id: string) {
    setMatches(current =>
      current && 'suggestions' in current
        ? {
            suggestions: current.suggestions.filter(s => s.id !== id),
          }
        : current,
    );
  }

  async function onApprove(suggestion: MatchSuggestion) {
    setApplyError(null);
    try {
      await send('jev-match-apply', {
        kind: suggestion.kind,
        transactionIds: [
          suggestion.transactions[0].id,
          suggestion.transactions[1].id,
        ],
      });
      dropMatch(suggestion.id);
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : String(error));
    }
  }

  async function onReject(suggestion: MatchSuggestion) {
    await send('jev-match-reject', {
      transactionIds: [
        suggestion.transactions[0].id,
        suggestion.transactions[1].id,
      ],
    });
    dropMatch(suggestion.id);
  }

  const kindLabel = {
    transfer: t('Transfer between accounts'),
    'card-payment': t('Credit card bill payment'),
    duplicate: t('Duplicate transaction'),
  };

  return (
    <Page header={t('JEV review')}>
      <View
        style={{ gap: 30, maxWidth: 700, flexShrink: 0, paddingBottom: 40 }}
      >
        {state && state.stats.applied > 0 && (
          <Text style={{ color: theme.pageTextSubdued }}>
            <Trans>
              The JEV categorized {{ applied: state.stats.applied }}{' '}
              transactions on its own and {{ kept: state.stats.kept }} still
              have that category.
            </Trans>
          </Text>
        )}

        <View style={{ gap: 10, flexShrink: 0 }}>
          <Text style={{ fontSize: 16, fontWeight: 600 }}>
            <Trans>Categories to confirm</Trans>
          </Text>
          {state && state.uncategorized.length > 0 && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
              }}
            >
              <Button onPress={onClassifyAll} isDisabled={isClassifying}>
                {isClassifying ? (
                  <Trans>The JEV is categorizing...</Trans>
                ) : (
                  <Trans>
                    Ask the JEV about {{ count: state.uncategorized.length }}{' '}
                    uncategorized transactions
                  </Trans>
                )}
              </Button>
            </View>
          )}
          {classifyResult && 'error' in classifyResult && (
            <Text style={{ color: theme.errorText }}>
              {classifyResult.error === 'not-configured' ? (
                <Trans>
                  The JEV is not configured. Add the OpenRouter key in Settings.
                </Trans>
              ) : (
                <Trans>The JEV could not be reached. Try again later.</Trans>
              )}
            </Text>
          )}
          {classifyResult && 'total' in classifyResult && (
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>
                The JEV categorized {{ applied: classifyResult.applied }} and
                suggested a category for{' '}
                {{ suggested: classifyResult.suggested }} of{' '}
                {{ total: classifyResult.total }} transactions.
              </Trans>
            </Text>
          )}
          {state && state.categorySuggestions.length === 0 && (
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>Nothing waiting for you.</Trans>
            </Text>
          )}
          {state?.categorySuggestions.map(item => (
            <JevSuggestionCard
              key={item.transactionId}
              transaction={item}
              onAccept={categoryId =>
                onAcceptCategory(item.transactionId, categoryId)
              }
              onDismiss={async () => {
                await send('jev-category-dismiss', {
                  transactionId: item.transactionId,
                });
                await refresh();
              }}
            />
          ))}
        </View>

        {state && state.uncategorized.length > 0 && (
          <View style={{ gap: 10, flexShrink: 0 }}>
            <Text style={{ fontSize: 16, fontWeight: 600 }}>
              <Trans>Still without a category</Trans>
            </Text>
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>
                Transactions the JEV has no suggestion for. The category you
                choose is used as an example for similar transactions.
              </Trans>
            </Text>
            {state.uncategorized.map(item => (
              <JevSuggestionCard
                key={item.transactionId}
                transaction={item}
                onAccept={categoryId =>
                  onAcceptCategory(item.transactionId, categoryId)
                }
              />
            ))}
          </View>
        )}

        <View style={{ gap: 10, flexShrink: 0 }}>
          <Text style={{ fontSize: 16, fontWeight: 600 }}>
            <Trans>Transfers, card payments and duplicates</Trans>
          </Text>
          <View style={{ flexDirection: 'row' }}>
            <Button onPress={onFindMatches} isDisabled={isSearching}>
              {isSearching ? (
                <Trans>Searching...</Trans>
              ) : (
                <Trans>Find matches</Trans>
              )}
            </Button>
          </View>
          {matches && 'error' in matches && (
            <Text style={{ color: theme.errorText }}>
              {matches.error === 'not-configured' ? (
                <Trans>
                  The JEV is not configured. Add the OpenRouter key in Settings.
                </Trans>
              ) : (
                <Trans>The JEV could not be reached. Try again later.</Trans>
              )}
            </Text>
          )}
          {matches &&
            'suggestions' in matches &&
            matches.suggestions.length === 0 && (
              <Text style={{ color: theme.pageTextSubdued }}>
                <Trans>No matches found.</Trans>
              </Text>
            )}
          {applyError && (
            <Text style={{ color: theme.errorText }}>{applyError}</Text>
          )}
          {matches &&
            'suggestions' in matches &&
            matches.suggestions.map(suggestion => (
              <Card key={suggestion.id}>
                <Text style={{ fontWeight: 600 }}>
                  {kindLabel[suggestion.kind]}{' '}
                  <Text
                    style={{ fontWeight: 400, color: theme.pageTextSubdued }}
                  >
                    {t('({{percent}}% sure)', {
                      percent: Math.round(suggestion.confidence * 100),
                    })}
                  </Text>
                </Text>
                <View style={{ flexDirection: 'row', gap: 20, flexShrink: 0 }}>
                  <Transaction trans={suggestion.transactions[0]} />
                  <Transaction trans={suggestion.transactions[1]} />
                </View>
                <View style={{ flexDirection: 'row', gap: 10, flexShrink: 0 }}>
                  <Button
                    variant="primary"
                    onPress={() => onApprove(suggestion)}
                  >
                    {suggestion.kind === 'duplicate' ? (
                      <Trans>Merge</Trans>
                    ) : (
                      <Trans>Make transfer</Trans>
                    )}
                  </Button>
                  <Button onPress={() => onReject(suggestion)}>
                    <Trans>Not related</Trans>
                  </Button>
                </View>
              </Card>
            ))}
        </View>

        <View style={{ gap: 10, flexShrink: 0 }}>
          <Text style={{ fontSize: 16, fontWeight: 600 }}>
            <Trans>Categorized by the JEV</Trans>
          </Text>
          {state && state.classified.length === 0 && (
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>
                Nothing yet. Transactions the JEV categorizes on its own after a
                bank sync show up here.
              </Trans>
            </Text>
          )}
          {state?.classified.map(item => (
            <Card key={item.transactionId}>
              <View style={{ gap: 2, flexShrink: 0 }}>
                <Text style={{ fontWeight: 600 }}>{item.description}</Text>
                <Text style={{ color: theme.pageTextSubdued }}>
                  {item.date} · {item.accountName}
                </Text>
                <FinancialText>{integerToCurrency(item.amount)}</FinancialText>
              </View>
              <Text>
                {item.isKept
                  ? t('Category: {{name}}', { name: item.appliedCategoryName })
                  : t('JEV chose {{applied}}, now {{current}}', {
                      applied: item.appliedCategoryName,
                      current: item.currentCategoryName ?? t('no category'),
                    })}
              </Text>
              {changingId === item.transactionId ? (
                <JevCategoryPicker
                  onSelect={categoryId =>
                    onAcceptCategory(item.transactionId, categoryId)
                  }
                  onCancel={() => setChangingId(null)}
                />
              ) : (
                <View style={{ flexDirection: 'row', flexShrink: 0 }}>
                  <Button onPress={() => setChangingId(item.transactionId)}>
                    <Trans>Change category</Trans>
                  </Button>
                </View>
              )}
            </Card>
          ))}
        </View>
      </View>
    </Page>
  );
}
